import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type {
  ApiResponse,
  AnyWave,
  ReceivedWave,
  SentWave,
  MutualWave,
  WaveUser,
} from '@/api/types';
import {
  Inbox,
  Send,
  Sparkles,
  Loader2,
  Check,
  X,
  Clock,
  MessageCircle,
  ChevronRight,
} from 'lucide-react';
import clsx from 'clsx';

type TabType = 'received' | 'sent' | 'mutual';

const TAB_CONFIG: Record<TabType, { label: string; icon: React.ElementType }> = {
  received: { label: 'Received', icon: Inbox },
  sent: { label: 'Sent', icon: Send },
  mutual: { label: 'Mutual', icon: Sparkles },
};

// The three wave endpoints return different shapes; normalize to one view model
// so the row render is shape-agnostic.
type WaveVM = {
  id: string;
  otherUser?: WaveUser;
  message?: string | null;
  createdAt?: string;
  expiresAt?: string;
  isPending: boolean;
  isMutual: boolean;
  // contactInfo was here until 2026-07-31. The server no longer sends it —
  // contact details never leave the backend; a mutual's payoff is now the
  // in-app conversation ("Say namaste" below).
};

function toWaveVM(wave: AnyWave, tab: TabType): WaveVM {
  if (tab === 'received') {
    const w = wave as ReceivedWave;
    return {
      id: w.id,
      otherUser: w.fromUser,
      message: w.message,
      createdAt: w.createdAt,
      expiresAt: w.expiresAt,
      isPending: true, // /waves/received is pre-filtered to pending server-side
      isMutual: false,
    };
  }
  if (tab === 'sent') {
    const w = wave as SentWave;
    return {
      id: w.id,
      otherUser: w.toUser,
      message: w.message,
      createdAt: w.createdAt,
      expiresAt: w.expiresAt,
      isPending: w.status === 'pending',
      isMutual: false,
    };
  }
  const w = wave as MutualWave;
  return {
    id: w.id,
    otherUser: w.user,
    createdAt: w.matchedAt,
    isPending: false,
    isMutual: true,
  };
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function getExpiryCountdown(dateString: string): string | null {
  const expiry = new Date(dateString);
  const now = new Date();
  const diffMs = expiry.getTime() - now.getTime();

  if (diffMs <= 0) return 'Expired';

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays}d left`;
  if (diffHours > 0) return `${diffHours}h left`;
  return 'Expiring soon';
}

export default function Waves() {
  const [activeTab, setActiveTab] = useState<TabType>('received');
  const queryClient = useQueryClient();

  // Fetch waves based on active tab
  const { data, isLoading } = useQuery({
    queryKey: ['waves', activeTab],
    queryFn: (): Promise<ApiResponse<AnyWave[]>> => {
      if (activeTab === 'received') {
        return api.get<ApiResponse<ReceivedWave[]>>('/identity-board/discovery/waves/received');
      } else if (activeTab === 'sent') {
        return api.get<ApiResponse<SentWave[]>>('/identity-board/discovery/waves/sent');
      } else {
        return api.get<ApiResponse<MutualWave[]>>('/identity-board/discovery/waves/mutual');
      }
    },
  });

  // Respond to wave mutation
  const respondToWave = useMutation({
    mutationFn: async ({ waveId, action }: { waveId: string; action: 'accept' | 'decline' }) => {
      return api.post<ApiResponse<unknown>>(`/identity-board/discovery/waves/${waveId}/respond`, {
        action,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waves'] });
      queryClient.invalidateQueries({ queryKey: ['matches'] });
    },
  });

  const waves = data?.data || [];

  // Count for badge — /waves/received returns only pending waves, so its length
  // IS the pending count.
  const { data: receivedData } = useQuery({
    queryKey: ['waves', 'received-count'],
    queryFn: () =>
      api.get<ApiResponse<ReceivedWave[]>>('/identity-board/discovery/waves/received'),
  });
  const pendingCount = receivedData?.data.length ?? 0;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-display font-bold">Waves</h1>
        <p className="text-gray-600">
          Manage your connection requests
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {(['received', 'sent', 'mutual'] as TabType[]).map((tab) => {
          const config = TAB_CONFIG[tab];
          const Icon = config.icon;
          const showBadge = tab === 'received' && pendingCount > 0;

          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={clsx(
                'px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 relative',
                activeTab === tab
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              )}
            >
              <Icon className="w-4 h-4" />
              {config.label}
              {showBadge && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && waves.length === 0 && (
        <div className="text-center py-20">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {activeTab === 'received' && <Inbox className="w-8 h-8 text-gray-400" />}
            {activeTab === 'sent' && <Send className="w-8 h-8 text-gray-400" />}
            {activeTab === 'mutual' && <Sparkles className="w-8 h-8 text-gray-400" />}
          </div>
          <h3 className="text-lg font-semibold mb-2">
            {activeTab === 'received' && 'No pending waves'}
            {activeTab === 'sent' && 'No waves sent'}
            {activeTab === 'mutual' && 'No mutual connections yet'}
          </h3>
          <p className="text-gray-600 mb-6">
            {activeTab === 'received' && "When someone sends you a wave, it'll show up here."}
            {activeTab === 'sent' && "Waves you send to others will appear here."}
            {activeTab === 'mutual' && "When both you and someone else wave, you'll connect!"}
          </p>
          {activeTab !== 'received' && (
            <Link to="/lab/identity-board/discovery" className="btn-primary">
              Browse Matches
            </Link>
          )}
        </div>
      )}

      {/* Waves List */}
      {waves.length > 0 && (
        <div className="space-y-3">
          {waves.map((wave) => {
            const { id, otherUser, message, createdAt, expiresAt, isPending, isMutual } =
              toWaveVM(wave, activeTab);
            const expiryText = expiresAt ? getExpiryCountdown(expiresAt) : null;
            const profileTo = `/lab/identity-board/discovery/${otherUser?.id}`;

            return (
              <div
                key={id}
                className={clsx(
                  'card p-4',
                  isMutual && 'bg-twins-light/30 border-twins/30'
                )}
              >
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <Link
                    to={profileTo}
                    className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-accent-400 flex items-center justify-center text-white font-semibold flex-shrink-0"
                  >
                    {otherUser?.name?.charAt(0) || '?'}
                  </Link>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Link
                        to={profileTo}
                        className="font-semibold hover:text-primary-600"
                      >
                        {otherUser?.name}
                      </Link>
                      {isMutual && (
                        <span className="text-xs text-twins-dark bg-twins-light px-2 py-0.5 rounded-full">
                          Connected
                        </span>
                      )}
                    </div>

                    {/* Message preview */}
                    {message && (
                      <div className="flex items-start gap-2 text-sm text-gray-600 mb-2">
                        <MessageCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-2">"{message}"</span>
                      </div>
                    )}

                    {/* Meta */}
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {createdAt && <span>{formatTimeAgo(createdAt)}</span>}
                      {isPending && expiryText && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {expiryText}
                        </span>
                      )}
                    </div>

                    {/* The payoff of a mutual wave: an in-app channel, never
                        contact details. Server-gated on the mutual wave — this
                        button is just the door handle. */}
                    {isMutual && otherUser && (
                      <SayNamaste peerId={otherUser.id} peerName={otherUser.name} />
                    )}
                  </div>

                  {/* Actions */}
                  {activeTab === 'received' && isPending && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => respondToWave.mutate({ waveId: id, action: 'accept' })}
                        disabled={respondToWave.isPending}
                        className="p-2 bg-twins-light text-twins-dark rounded-lg hover:bg-twins/20 transition-colors"
                        title="Accept"
                      >
                        <Check className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => respondToWave.mutate({ waveId: id, action: 'decline' })}
                        disabled={respondToWave.isPending}
                        className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                        title="Decline"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  )}

                  {activeTab === 'sent' && isPending && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded flex-shrink-0">
                      Pending
                    </span>
                  )}

                  {isMutual && (
                    <Link
                      to={profileTo}
                      className="p-2 text-gray-400 hover:text-gray-600"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/**
 * The mutual's payoff since contact details stopped leaving the server:
 * open (or resume) the in-app conversation and send the first message right
 * here. The button is only a door handle — the mutual-wave gate lives
 * server-side, and this surface exists so that gate gets exercised.
 */
function SayNamaste({ peerId, peerName }: { peerId: string; peerName: string }) {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function openChannel() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<ApiResponse<{ conversation: { id: string } }>>(
        '/conversations',
        { peerUserId: peerId },
      );
      setConversationId(res.data.conversation.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open the conversation');
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    if (!conversationId || !text.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/conversations/${conversationId}/messages`, { body: text.trim() });
      setSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 pt-2 border-t border-twins/20">
      {!conversationId && (
        <button
          onClick={openChannel}
          disabled={busy}
          className="flex items-center gap-2 text-sm font-medium text-twins-dark hover:underline disabled:opacity-50"
        >
          <MessageCircle className="w-4 h-4" />
          Say namaste
        </button>
      )}
      {conversationId && !sent && (
        <div className="flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Message ${peerName}…`}
            className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
          />
          <button
            onClick={send}
            disabled={busy || !text.trim()}
            className="rounded-lg bg-twins-light px-3 py-1.5 text-sm font-medium text-twins-dark disabled:opacity-50"
          >
            Send
          </button>
        </div>
      )}
      {sent && (
        <p className="text-xs text-gray-500">
          Sent — the conversation continues in-app. No contact details change hands.
        </p>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
